/**
 * CWG Multi-Station Weather Module
 * Uses WebSocket to get real-time weather data for multiple Tempest stations.
 * All current conditions are derived from obs_st messages (obs array + summary).
 */

const WS_URL = 'wss://ws.weatherflow.com/swd/data';
const STATIONS_ENDPOINT = '/internal/cwg/stations';
const CONDITIONS_ENDPOINT = '/internal/cwg/conditions';
const ICON_BASE_URL = 'https://tempestwx.com/images/Updated';
const CONDITIONS_REFRESH_MS = 5 * 60 * 1000; // 5 minutes

let socket = null;
let stations = [];
let apiKey = null;
let reconnectAttempts = 0;
const MAX_RECONNECT_ATTEMPTS = 10;
const RECONNECT_DELAY = 5000;

// Official Tempest icon slugs and conditions text keyed by station id,
// populated from the /internal/cwg/conditions endpoint. When present these
// override the values derived heuristically from raw observations.
const officialIcons = {};
const officialConditions = {};
const officialFeelsLike = {};
const officialSeaLevelPressure = {};
const officialDaily = {};
let conditionsTimer = null;

/**
 * obs_st array indices (Tempest observation)
 */
const OBS = {
  TIMESTAMP: 0,
  WIND_LULL: 1,
  WIND_AVG: 2,
  WIND_GUST: 3,
  WIND_DIR: 4,
  WIND_INTERVAL: 5,
  PRESSURE: 6,
  AIR_TEMP: 7,
  HUMIDITY: 8,
  ILLUMINANCE: 9,
  UV: 10,
  SOLAR_RADIATION: 11,
  RAIN_LAST_MIN: 12,
  PRECIP_TYPE: 13,
  STRIKE_AVG_DIST: 14,
  STRIKE_COUNT: 15,
  BATTERY: 16,
  REPORT_INTERVAL: 17,
  LOCAL_DAY_RAIN: 18,
};

/**
 * Initialize the CWG module
 */
export async function initializeCwg() {
  try {
    const response = await fetch(STATIONS_ENDPOINT);
    const data = await response.json();

    if (data.status === 'success') {
      stations = data.stations;
      apiKey = data.apiKey;

      // Update station cards with metadata (location names)
      updateStationMetadata(stations);

      // Connect to WebSocket for all data
      connectWebSocket();

      // Fetch official Tempest icons now and refresh periodically
      fetchConditions();
      conditionsTimer = setInterval(fetchConditions, CONDITIONS_REFRESH_MS);

      // Enable click-to-expand forecast modal on station cards
      setupForecastModal();

      // Clean up on page close/navigate
      window.addEventListener('beforeunload', stopCwg);
    } else {
      console.error('[CWG] Failed to load stations');
      showError('Failed to load station data');
    }
  } catch (error) {
    console.error('[CWG] Error initializing:', error);
    showError('Failed to initialize');
  }
}

/**
 * Update station cards with metadata (location, etc.)
 */
function updateStationMetadata(stationList) {
  stationList.forEach(station => {
    const locationEl = document.getElementById(`location-${station.stationId}`);
    if (locationEl) {
      locationEl.textContent = station.location || 'Unknown location';
    }

    if (station.status === 'error') {
      setStationError(station.stationId, station.error);
    }
  });
}

/**
 * Connect to WeatherFlow WebSocket
 */
function connectWebSocket() {
  if (!apiKey) {
    console.error('[CWG] No API key available');
    return;
  }

  const wsUrl = `${WS_URL}?api_key=${apiKey}`;
  socket = new WebSocket(wsUrl);

  socket.onopen = () => {
    reconnectAttempts = 0;
    updateConnectionStatus(true);

    stations.forEach(station => {
      if (station.deviceId) {
        subscribeToDevice(station.deviceId);
      }
    });
  };

  socket.onmessage = (event) => {
    try {
      const message = JSON.parse(event.data);
      handleWebSocketMessage(message);
    } catch (error) {
      console.error('[CWG] Error parsing message:', error);
    }
  };

  socket.onclose = () => {
    updateConnectionStatus(false);
    attemptReconnect();
  };

  socket.onerror = (error) => {
    console.error('[CWG] WebSocket error:', error);
  };
}

/**
 * Subscribe to a device for observations
 */
function subscribeToDevice(deviceId) {
  if (!socket || socket.readyState !== WebSocket.OPEN) {
    return;
  }

  socket.send(JSON.stringify({
    type: 'listen_start',
    device_id: deviceId,
    id: `cwg-${deviceId}`,
  }));
}

/**
 * Handle incoming WebSocket messages
 */
function handleWebSocketMessage(message) {
  switch (message.type) {
  case 'obs_st':
    handleObservation(message);
    break;
  case 'rapid_wind':
    handleRapidWind(message);
    break;
  case 'ack':
    break;
  default:
    break;
  }
}

/**
 * Convert Celsius to Fahrenheit
 */
function cToF(c) {
  return c !== null && c !== undefined ? (c * 9 / 5) + 32 : null;
}

/**
 * Convert m/s to mph
 */
function msToMph(ms) {
  return ms !== null && ms !== undefined ? ms * 2.237 : null;
}

/**
 * Convert mm to inches
 */
function mmToIn(mm) {
  return mm !== null && mm !== undefined ? mm / 25.4 : null;
}

/**
 * Convert wind degrees to cardinal direction
 */
function degreesToCardinal(deg) {
  if (deg === null || deg === undefined) {
    return '';
  }
  const dirs = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE',
    'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
  return dirs[Math.round(deg / 22.5) % 16];
}

/**
 * Handle observation message (obs_st) — the primary data source for all fields.
 */
function handleObservation(message) {
  const deviceId = message.device_id;
  const station = stations.find(s => s.deviceId === deviceId);

  if (!station) {
    return;
  }

  const stationId = station.stationId;
  const obs = message.obs?.[0];
  const summary = message.summary || {};

  if (!obs) {
    return;
  }

  // Extract and convert values
  const tempC = obs[OBS.AIR_TEMP];
  const tempF = cToF(tempC);
  const humidity = obs[OBS.HUMIDITY];
  const windAvgMph = msToMph(obs[OBS.WIND_AVG]);
  const windGustMph = msToMph(obs[OBS.WIND_GUST]);
  const windDir = obs[OBS.WIND_DIR];
  const pressureMb = obs[OBS.PRESSURE];
  const solarRadiation = obs[OBS.SOLAR_RADIATION];
  const rainLastMin = obs[OBS.RAIN_LAST_MIN] || 0;
  const localDayRainMm = obs[OBS.LOCAL_DAY_RAIN];
  const precipIn = mmToIn(localDayRainMm);

  // Summary fields (already in Celsius from API)
  const feelsLikeF = cToF(summary.feels_like);
  const dewPointF = cToF(summary.dew_point);
  const pressureTrend = summary.pressure_trend || null;
  const strikeCount3h = summary.strike_count_3h ?? null;

  // --- Update UI ---

  // Temperature
  const tempEl = document.getElementById(`temp-${stationId}`);
  if (tempEl && tempF !== null) {
    tempEl.textContent = Math.round(tempF);
  }

  // Conditions text — prefer the official Tempest value, fall back to heuristic.
  const conditionsEl = document.getElementById(`conditions-${stationId}`);
  if (conditionsEl) {
    conditionsEl.textContent = officialConditions[stationId]
      || getConditionsText(rainLastMin, humidity, windAvgMph, tempC);
  }

  // Prefer the official Tempest icon when available, otherwise fall back to
  // the icon derived heuristically from the raw observation.
  const icon = officialIcons[stationId]
    || getWeatherIcon(rainLastMin, humidity, windAvgMph, solarRadiation, strikeCount3h);
  setStationIcon(stationId, icon);

  // Wind (avg + gust + direction)
  const windEl = document.getElementById(`wind-${stationId}`);
  if (windEl && windAvgMph !== null) {
    const cardinal = degreesToCardinal(windDir);
    const avg = Math.round(windAvgMph);
    const gust = windGustMph ? Math.round(windGustMph) : null;
    const windText = gust && gust > avg
      ? `${cardinal} ${avg} (G${gust}) mph`
      : `${cardinal} ${avg} mph`;
    windEl.textContent = windText;
  }

  // Dew point
  const dewpointEl = document.getElementById(`dewpoint-${stationId}`);
  if (dewpointEl && dewPointF !== null) {
    dewpointEl.textContent = `${Math.round(dewPointF)}\u00b0`;
  }

  // Precipitation
  const precipEl = document.getElementById(`precip-${stationId}`);
  if (precipEl && precipIn !== null) {
    precipEl.textContent = `${precipIn.toFixed(2)}"`;
  }

  // Pressure + trend
  const pressureEl = document.getElementById(`pressure-${stationId}`);
  if (pressureEl && pressureMb !== null) {
    const trendSymbol = pressureTrend === 'rising' ? '\u2191' :
      pressureTrend === 'falling' ? '\u2193' : '';
    pressureEl.textContent = `${Math.round(pressureMb)} ${trendSymbol}`.trim();
  }

  // Lightning
  const lightningEl = document.getElementById(`lightning-${stationId}`);
  if (lightningEl) {
    if (strikeCount3h !== null && strikeCount3h > 0) {
      lightningEl.textContent = `${strikeCount3h} (3hr)`;
      lightningEl.style.color = '#ffd700';
    } else {
      lightningEl.textContent = '0';
      lightningEl.style.color = '';
    }
  }

  // Feels like \u2014 prefer the official Tempest value, fall back to the WS summary.
  const feels = officialFeelsLike[stationId] ?? feelsLikeF;
  setStationFeelsLike(stationId, feels);

  // Humidity
  const humidityEl = document.getElementById(`humidity-${stationId}`);
  if (humidityEl && humidity !== null) {
    humidityEl.textContent = `${humidity}%`;
  }

  // Remove error state
  const cardEl = document.querySelector(`[data-station-id="${stationId}"]`);
  if (cardEl) {
    cardEl.classList.remove('cwg-card-error');
  }
}

/**
 * Handle rapid wind message
 */
function handleRapidWind(message) {
  const deviceId = message.device_id;
  const station = stations.find(s => s.deviceId === deviceId);

  if (!station) {
    return;
  }

  const stationId = station.stationId;
  const windMs = message.ob?.[1];
  const windDir = message.ob?.[2];

  if (windMs === undefined) {
    return;
  }

  const windMph = msToMph(windMs);
  const windEl = document.getElementById(`wind-${stationId}`);

  if (windEl && windMph !== null) {
    const cardinal = degreesToCardinal(windDir);
    windEl.textContent = `${cardinal} ${Math.round(windMph)} mph`;
  }
}

/**
 * Get conditions text based on weather data
 */
function getConditionsText(rainAccum, humidity, windSpeed, tempC) {
  if (rainAccum > 0) {
    if (tempC !== null && tempC <= 0) {
      return 'Snow';
    }
    return 'Rain';
  }
  if (windSpeed !== null && windSpeed > 25) {
    return 'Windy';
  }
  if (humidity === null) {
    return '';
  }
  if (humidity > 95) {
    return 'Foggy';
  }
  if (humidity > 90) {
    return 'Very Humid';
  }
  if (humidity > 70) {
    return 'Humid';
  }
  if (humidity > 50) {
    return 'Comfortable';
  }
  if (humidity > 30) {
    return 'Dry';
  }
  return 'Very Dry';
}

/**
 * Set a station's weather icon image from an icon slug.
 */
function setStationIcon(stationId, icon) {
  const iconEl = document.getElementById(`icon-${stationId}`);
  if (iconEl && icon) {
    iconEl.src = `${ICON_BASE_URL}/${icon}.svg`;
  }
}

/**
 * Set a station's conditions text.
 */
function setStationConditions(stationId, conditions) {
  const conditionsEl = document.getElementById(`conditions-${stationId}`);
  if (conditionsEl && conditions) {
    conditionsEl.textContent = conditions;
  }
}

/**
 * Set a station's "feels like" temperature (Fahrenheit).
 */
function setStationFeelsLike(stationId, feelsF) {
  const feelsEl = document.getElementById(`feels-${stationId}`);
  if (feelsEl && feelsF !== null && feelsF !== undefined) {
    feelsEl.textContent = `${Math.round(feelsF)}°`;
  }
}

/**
 * Set a station's sea-level pressure (mb).
 */
function setStationSeaLevelPressure(stationId, pressureMb) {
  const slpEl = document.getElementById(`slp-${stationId}`);
  if (slpEl && pressureMb !== null && pressureMb !== undefined) {
    slpEl.textContent = `${Math.round(pressureMb)}`;
  }
}

/**
 * Reflect a station's online/offline status via the header status dot and a
 * dimmed card. A null status (unknown) leaves the indicator neutral.
 */
function setStationOnline(stationId, online) {
  const dotEl = document.getElementById(`status-${stationId}`);
  if (dotEl) {
    dotEl.classList.remove('online', 'offline');
    if (online === true) {
      dotEl.classList.add('online');
      dotEl.title = 'Online';
    } else if (online === false) {
      dotEl.classList.add('offline');
      dotEl.title = 'Offline';
    }
  }

  const cardEl = document.querySelector(`[data-station-id="${stationId}"]`);
  if (cardEl) {
    cardEl.classList.toggle('cwg-card-offline', online === false);
  }
}

/**
 * Fetch official Tempest current-conditions icon + text for all stations and
 * apply them. Falls back silently to the heuristic values (set on each
 * observation) on failure.
 */
async function fetchConditions() {
  try {
    const response = await fetch(CONDITIONS_ENDPOINT);
    const data = await response.json();

    if (data.status === 'success' && data.conditions) {
      Object.entries(data.conditions).forEach(([stationId, cond]) => {
        if (!cond) {
          return;
        }
        if (cond.icon) {
          officialIcons[stationId] = cond.icon;
          setStationIcon(stationId, cond.icon);
        }
        if (cond.conditions) {
          officialConditions[stationId] = cond.conditions;
          setStationConditions(stationId, cond.conditions);
        }
        if (cond.feelsLike !== null && cond.feelsLike !== undefined) {
          officialFeelsLike[stationId] = cond.feelsLike;
          setStationFeelsLike(stationId, cond.feelsLike);
        }
        if (cond.seaLevelPressure !== null && cond.seaLevelPressure !== undefined) {
          officialSeaLevelPressure[stationId] = cond.seaLevelPressure;
          setStationSeaLevelPressure(stationId, cond.seaLevelPressure);
        }
        if (Array.isArray(cond.daily) && cond.daily.length > 0) {
          officialDaily[stationId] = cond.daily;
        }
        setStationOnline(stationId, cond.online);
      });
    }
  } catch (error) {
    console.error('[CWG] Error fetching conditions:', error);
  }
}

/**
 * Wire up the click-to-expand daily forecast modal on the station grid.
 */
function setupForecastModal() {
  const grid = document.getElementById('cwg-stations-grid');
  if (grid) {
    grid.addEventListener('click', (e) => {
      // Let the station-name link navigate normally.
      if (e.target.closest('a')) {
        return;
      }
      const card = e.target.closest('.cwg-card');
      const stationId = card && card.getAttribute('data-station-id');
      if (stationId) {
        openForecastModal(stationId);
      }
    });
  }

  const closeBtn = document.getElementById('cwg-modal-close');
  const backdrop = document.getElementById('cwg-modal-backdrop');
  if (closeBtn) {
    closeBtn.addEventListener('click', closeForecastModal);
  }
  if (backdrop) {
    backdrop.addEventListener('click', closeForecastModal);
  }
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      closeForecastModal();
    }
  });
}

/**
 * Format a daily forecast column label (weekday, or "Today" for the first day).
 */
function formatDayLabel(dayStart, index) {
  if (index === 0) {
    return 'Today';
  }
  if (dayStart) {
    return new Date(dayStart * 1000).toLocaleDateString('en-US', { weekday: 'short' });
  }
  return '';
}

/**
 * Build a single day column element for the forecast strip.
 */
function buildDayColumn(day, index) {
  const col = document.createElement('div');
  col.className = 'cwg-forecast-day';

  const name = document.createElement('div');
  name.className = 'cwg-forecast-dayname';
  name.textContent = formatDayLabel(day.dayStart, index);
  col.appendChild(name);

  if (day.icon) {
    const img = document.createElement('img');
    img.className = 'cwg-forecast-icon';
    img.src = `${ICON_BASE_URL}/${day.icon}.svg`;
    img.alt = day.conditions || '';
    col.appendChild(img);
  }

  const temps = document.createElement('div');
  temps.className = 'cwg-forecast-temps';
  const hi = document.createElement('span');
  hi.className = 'cwg-forecast-high';
  hi.textContent = (day.high !== null && day.high !== undefined) ? `${Math.round(day.high)}°` : '--';
  const lo = document.createElement('span');
  lo.className = 'cwg-forecast-low';
  lo.textContent = (day.low !== null && day.low !== undefined) ? `${Math.round(day.low)}°` : '--';
  temps.appendChild(hi);
  temps.appendChild(lo);
  col.appendChild(temps);

  const precip = document.createElement('div');
  precip.className = 'cwg-forecast-precip';
  const p = day.precipProbability;
  precip.textContent = (p !== null && p !== undefined && p > 0) ? `💧 ${p}%` : '';
  col.appendChild(precip);

  return col;
}

/**
 * Open the forecast modal for a station, populating the daily strip.
 */
function openForecastModal(stationId) {
  const modal = document.getElementById('cwg-forecast-modal');
  const titleEl = document.getElementById('cwg-modal-title');
  const stripEl = document.getElementById('cwg-modal-daily');
  if (!modal || !stripEl) {
    return;
  }

  const station = stations.find(s => String(s.stationId) === String(stationId));
  if (titleEl) {
    titleEl.textContent = station ? `${station.name} — Forecast` : 'Forecast';
  }

  stripEl.innerHTML = '';
  const daily = officialDaily[stationId];
  if (!daily || daily.length === 0) {
    const empty = document.createElement('p');
    empty.className = 'cwg-forecast-empty';
    empty.textContent = 'Forecast not available yet.';
    stripEl.appendChild(empty);
  } else {
    daily.forEach((day, i) => stripEl.appendChild(buildDayColumn(day, i)));
  }

  modal.hidden = false;
}

/**
 * Close the forecast modal.
 */
function closeForecastModal() {
  const modal = document.getElementById('cwg-forecast-modal');
  if (modal) {
    modal.hidden = true;
  }
}

/**
 * Get weather icon based on conditions
 */
function getWeatherIcon(rainAccum, humidity, windSpeed, solarRadiation, strikeCount3h) {
  const hour = new Date().getHours();
  const isNight = hour < 6 || hour > 20;

  if (strikeCount3h > 0 && rainAccum > 0) {
    return 'thunderstorm';
  }
  if (rainAccum > 0) {
    return 'rainy';
  }
  if (windSpeed > 25) {
    return 'windy';
  }
  if (humidity > 95) {
    return 'foggy';
  }
  if (!isNight && solarRadiation !== null && solarRadiation < 100) {
    return 'cloudy';
  }
  if (!isNight && solarRadiation !== null && solarRadiation < 400) {
    return 'partly-cloudy-day';
  }
  if (isNight && solarRadiation !== null && solarRadiation === 0 && humidity > 70) {
    return 'partly-cloudy-night';
  }
  return isNight ? 'clear-night' : 'clear-day';
}

/**
 * Attempt to reconnect after disconnect
 */
function attemptReconnect() {
  if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
    console.error('[CWG] Max reconnect attempts reached');
    showError('Connection lost');
    return;
  }

  reconnectAttempts++;

  setTimeout(() => {
    connectWebSocket();
  }, RECONNECT_DELAY);
}

/**
 * Update connection status indicator
 */
function updateConnectionStatus(connected) {
  const timeEl = document.getElementById('cwg-update-time');
  if (timeEl) {
    if (connected) {
      timeEl.textContent = 'Connected';
      timeEl.style.color = '#64ffda';
    } else {
      timeEl.textContent = 'Disconnected';
      timeEl.style.color = '#ff6b6b';
    }
  }
}

/**
 * Set error state for a station card
 */
function setStationError(stationId, errorMsg) {
  const cardEl = document.querySelector(`[data-station-id="${stationId}"]`);
  if (cardEl) {
    cardEl.classList.add('cwg-card-error');
  }

  const tempEl = document.getElementById(`temp-${stationId}`);
  if (tempEl) {
    tempEl.textContent = '--';
  }

  const conditionsEl = document.getElementById(`conditions-${stationId}`);
  if (conditionsEl) {
    conditionsEl.textContent = errorMsg || 'Error';
  }
}

/**
 * Show global error message
 */
function showError(message) {
  const timeEl = document.getElementById('cwg-update-time');
  if (timeEl) {
    timeEl.textContent = message;
    timeEl.style.color = '#ff6b6b';
  }
}

/**
 * Stop WebSocket connection (for cleanup)
 */
export function stopCwg() {
  if (socket) {
    socket.close();
    socket = null;
  }
  if (conditionsTimer) {
    clearInterval(conditionsTimer);
    conditionsTimer = null;
  }
}
